import { useState } from "react";
import { useSiteName } from "@/hooks/useSiteSettings";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Twitter, Instagram, Youtube, Linkedin, Send, MapPin } from "lucide-react";
// 128px copy of the mascot: shown at 36-56px, the 512px PNG was 270 KB.
import bullioFace from "@/assets/bullio-face-sm.webp";
import { isChromelessRoute } from "@/lib/chromeless";
import { CHECKOUT_URL } from "@/config/checkout";

/**
 * Where the company has offices. Plain data, so a third one is one more entry
 * and the markup below does not change. The `<address>` element is the right
 * one for this — contact details for the page's owner — and the maps link uses
 * the street address itself as the query, so it opens the right place whatever
 * the visitor's maps app is.
 */
const OFFICES = [
  {
    city: "Toronto",
    lines: ["First Canadian Place", "100 King St W", "Toronto, ON M5H 1H1", "Canada"],
    query: "First Canadian Place, 100 King St W, Toronto, ON M5H 1H1, Canada",
  },
  {
    city: "Amsterdam",
    lines: ["Atrium Building", "Strawinskylaan 3051", "1077 ZX Amsterdam", "Netherlands"],
    query: "Atrium Building, Strawinskylaan 3051, 1077 ZX Amsterdam, Netherlands",
  },
] as const;

const Footer = () => {
  // Admin-managed in Settings; was hardcoded in both places before.
  const siteName = useSiteName();
  const [email, setEmail] = useState("");
  const { user } = useAuth();
  const { pathname } = useLocation();

  // Hide footer when logged in, inside admin, or on a chromeless route (/lp
  // ends in its own minimal legal strip instead — a full sitemap of links
  // under a campaign page is a menu of ways to leave without converting).
  if (user || pathname.startsWith("/admin") || isChromelessRoute(pathname)) return null;

  const handleNewsletter = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      toast.error("Please enter a valid email.");
      return;
    }
    toast.success("Subscribed! Welcome to the herd.");
    setEmail("");
  };

  const socials = [
    { Icon: Twitter, href: "https://twitter.com", label: "Twitter" },
    { Icon: Instagram, href: "https://instagram.com", label: "Instagram" },
    { Icon: Youtube, href: "https://youtube.com", label: "YouTube" },
    { Icon: Linkedin, href: "https://linkedin.com", label: "LinkedIn" },
  ];

  return (
    <footer className="border-t border-border bg-gradient-to-b from-muted to-background">
      <div className="mx-auto container-page px-4 py-16 sm:px-6">
        <div className="grid gap-12 sm:grid-cols-2 lg:grid-cols-4">
          {/* Brand */}
          <div className="space-y-4">
            <Link to="/" className="flex items-center gap-3 group">
              <img
                src={bullioFace}
                alt=""
                width={56}
                height={56}
                className="h-14 w-14 rounded-2xl object-cover shadow-lg ring-2 ring-secondary/30 transition-transform group-hover:scale-110 group-hover:rotate-3"
              />
              <span className="text-xl font-bold text-primary">{siteName}</span>
            </Link>
            <p className="text-sm leading-relaxed text-muted-foreground">
              Professional trading education. No hype, no shortcuts, just skill.
            </p>
            <div className="flex gap-2 pt-1">
              {socials.map(({ Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noreferrer noopener"
                  aria-label={label}
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-background border border-border text-muted-foreground transition-all duration-300 hover:text-secondary hover:border-secondary hover:-translate-y-0.5 hover:shadow-md"
                >
                  <Icon size={16} />
                </a>
              ))}
            </div>
          </div>

          {/* Links */}
          <div>
            <h3 className="mb-4 text-sm font-semibold text-foreground">Academy</h3>
            <div className="space-y-2.5 text-sm">
              {[
                { to: "/curriculum", label: "Curriculum" },
                { to: "/insights", label: "Insights" },
                // Buying, like every other Enroll on the site. Not /enroll, which
                // is a free lead form. An external URL, so it renders as a plain
                // anchor below: a Link would treat it as an in-app path.
                { href: CHECKOUT_URL, label: "Enroll" },
                { to: "/about", label: "About" },
                { to: "/learning-experience", label: "Learning Experience" },
              ].map((l) =>
                "href" in l ? (
                  <a key={l.href} href={l.href} className="block text-muted-foreground hover:text-secondary transition-colors">
                    {l.label}
                  </a>
                ) : (
                  <Link key={l.to} to={l.to} className="block text-muted-foreground hover:text-secondary transition-colors">
                    {l.label}
                  </Link>
                ),
              )}
            </div>
          </div>

          {/* Support */}
          <div>
            <h3 className="mb-4 text-sm font-semibold text-foreground">Support</h3>
            <div className="space-y-2.5 text-sm">
              {[
                { to: "/contact", label: "Contact Us" },
                { to: "/ai-assistant", label: "AI Assistant" },
                { to: "/news", label: "News Portal" },
                { to: "/privacy", label: "Privacy Policy" },
                { to: "/terms", label: "Terms & Conditions" },
                { to: "/aml", label: "AML" },
              ].map((l) => (
                <Link key={l.to} to={l.to} className="block text-muted-foreground hover:text-secondary transition-colors">
                  {l.label}
                </Link>
              ))}
              <a href="mailto:office@bullio.org" className="block text-muted-foreground hover:text-secondary transition-colors">
                office@bullio.org
              </a>
            </div>
          </div>

          {/* Newsletter */}
          <div className="sm:col-span-2 lg:col-span-1">
            <h3 className="mb-4 text-sm font-semibold text-foreground">Newsletter</h3>
            <p className="mb-4 text-sm text-muted-foreground">Weekly market insights and trading tips.</p>
            <form onSubmit={handleNewsletter} className="flex flex-col gap-2 sm:flex-row">
              <Input
                type="email"
                placeholder="you@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 text-sm"
                maxLength={255}
              />
              <Button type="submit" className="shrink-0 bg-secondary hover:bg-secondary/90 btn-premium gap-1.5">
                <Send size={14} /> Join
              </Button>
            </form>
          </div>
        </div>

        {/* Offices */}
        <div className="mt-12 border-t border-border pt-8">
          <h3 className="mb-4 text-sm font-semibold text-foreground">Our Offices</h3>
          <div className="grid gap-6 sm:grid-cols-2">
            {OFFICES.map((o) => (
              <address key={o.city} className="flex gap-3 text-sm not-italic text-muted-foreground">
                <MapPin size={16} className="mt-0.5 shrink-0 text-secondary" aria-hidden />
                <div>
                  <div className="font-medium text-foreground">{o.city}</div>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(o.query)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 block leading-relaxed transition-colors hover:text-secondary"
                  >
                    {o.lines.map((line) => (
                      <span key={line} className="block">
                        {line}
                      </span>
                    ))}
                  </a>
                </div>
              </address>
            ))}
          </div>
        </div>

        {/* Copyright */}
        <div className="mt-8 flex flex-col items-center justify-between gap-4 border-t border-border pt-8 sm:flex-row">
          <p className="text-xs text-muted-foreground">© 2026 {siteName}. All rights reserved.</p>
          <div className="flex gap-5 text-xs text-muted-foreground">
            <Link to="/privacy" className="hover:text-secondary transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-secondary transition-colors">Terms</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
